// OwnershipTransfer.js
import React, { useState } from "react";
import { FaUser, FaExchangeAlt, FaInfoCircle, FaEnvelope, FaSpinner, FaExclamationTriangle } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { useArenaAuth } from "../../context/ArenaAuthContext";
import { validateEmail } from "../../utils/passwordValidation";

const OwnershipTransfer = () => {
  const [newOwnerEmail, setNewOwnerEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [transferMessage, setTransferMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    newOwnerEmail: "",
    confirmEmail: "",
    general: ""
  });
  const { arenaUser } = useArenaAuth();

  const validateForm = () => {
    const newErrors = {
      newOwnerEmail: "",
      confirmEmail: "",
      general: ""
    };

    // Validate new owner email
    if (!newOwnerEmail.trim()) {
      newErrors.newOwnerEmail = "New owner's email is required";
    } else {
      const trimmedEmail = newOwnerEmail.trim();
      const emailValidation = validateEmail(trimmedEmail);
      if (emailValidation) {
        newErrors.newOwnerEmail = emailValidation;
      }
    }

    // Validate confirm email
    if (!confirmEmail.trim()) {
      newErrors.confirmEmail = "Please confirm the email address";
    }

    // Check if user is trying to transfer to themselves
    if (newOwnerEmail.trim() && arenaUser?.email && newOwnerEmail.toLowerCase() === arenaUser.email.toLowerCase()) {
      newErrors.newOwnerEmail = "You cannot transfer ownership to yourself";
    }

    setErrors(newErrors);
    return !newErrors.newOwnerEmail && !newErrors.confirmEmail;
  };

  const handleTransfer = async (e) => {
    e.preventDefault();

    // Clear previous messages and errors
    setTransferMessage("");
    setErrors({ newOwnerEmail: "", confirmEmail: "", general: "" });

    // Basic validation first
    if (!newOwnerEmail.trim()) {
      toast.error("Please enter the new owner's email address.");
      return;
    }

    if (!confirmEmail.trim()) {
      toast.error("Please confirm the new owner's email address.");
      return;
    }

    // Check if emails match
    if (newOwnerEmail.trim() !== confirmEmail.trim()) {
      toast.error("Email addresses do not match. Please try again.");
      return;
    }

    // Validate form (this will check email format and other validations)
    const isFormValid = validateForm();
    if (!isFormValid) {
      return;
    }

    const arenaId = arenaUser?.arenaId;
    if (!arenaId) {
      toast.error("Error: Arena ID not found. Please log in again.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/initiate-ownership-transfer`,
        {
          arenaId: arenaId,
          newOwnerEmail: newOwnerEmail.trim()
        },
        { withCredentials: true }
      );

      toast.success(response.data.message || "Ownership transfer initiated successfully!");
      setNewOwnerEmail("");
      setConfirmEmail("");
      setErrors({ newOwnerEmail: "", confirmEmail: "", general: "" });
      setTransferMessage("Transfer request sent successfully. The new owner will receive an email notification.");
    } catch (error) {
      console.error("Error initiating ownership transfer:", error);
      if (error.response && error.response.data && error.response.data.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("An error occurred while processing your request. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
          <FaExchangeAlt className="text-[#0071bc] text-lg sm:text-xl md:text-2xl" />
          <span className="truncate">Transfer Ownership</span>
        </h2>
        <p className="text-gray-600 text-xs sm:text-sm md:text-base">Transfer arena ownership to another registered user</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 md:mb-8">
        <div className="flex items-start gap-2 sm:gap-3">
          <FaExclamationTriangle className="text-amber-600 text-base sm:text-lg md:text-xl mt-0.5 sm:mt-1 flex-shrink-0" />
          <div>
            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-amber-800 mb-1 sm:mb-2">Important Notice</h3>
            <p className="text-amber-700 leading-relaxed text-xs sm:text-sm md:text-base">
              Transferring ownership will give full control of this arena to another user. 
              This action cannot be undone. The new owner must be a registered user in the system.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white p-3 sm:p-4 md:p-6 lg:p-8 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200">
        <form onSubmit={handleTransfer} className="space-y-4 sm:space-y-6">
          <div>
            <label htmlFor="newOwnerEmail" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
              <FaEnvelope className="text-[#0071bc] text-xs sm:text-sm" />
              New Owner's Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="newOwnerEmail"
              value={newOwnerEmail}
              onChange={(e) => {
                setNewOwnerEmail(e.target.value);
                // Clear error when user starts typing
                if (errors.newOwnerEmail) {
                  setErrors(prev => ({ ...prev, newOwnerEmail: "" }));
                }
              }}
              className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${errors.newOwnerEmail ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Enter new owner's email address"
            />
            {errors.newOwnerEmail && (
              <div className="text-red-500 text-xs sm:text-sm mt-1">{errors.newOwnerEmail}</div>
            )}
          </div>

          <div>
            <label htmlFor="confirmEmail" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 flex items-center gap-1 sm:gap-2">
              <FaEnvelope className="text-green-500 text-xs sm:text-sm" />
              <span className="truncate">Confirm New Owner's Email</span> <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="confirmEmail"
              value={confirmEmail}
              onChange={(e) => {
                setConfirmEmail(e.target.value);
                // Clear error when user starts typing
                if (errors.confirmEmail) {
                  setErrors(prev => ({ ...prev, confirmEmail: "" }));
                }
              }}
              className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0071bc] focus:border-transparent transition-all duration-300 ${errors.confirmEmail ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="Confirm new owner's email address"
            />
            {errors.confirmEmail && (
              <div className="text-red-500 text-xs sm:text-sm mt-1">{errors.confirmEmail}</div>
            )}
          </div>

          <div className="pt-2 sm:pt-4">
            <button 
              type="submit" 
              className="w-full bg-red-600 text-white px-4 sm:px-6 md:px-8 py-3 sm:py-4 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:bg-red-700 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FaSpinner className="animate-spin text-sm sm:text-base md:text-lg" />
                  <span className="truncate">Processing Transfer...</span>
                </>
              ) : (
                <>
                  <FaExchangeAlt className="text-sm sm:text-base md:text-lg" />
                  <span className="truncate">Transfer Ownership</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {transferMessage && (
        <div className="mt-4 sm:mt-6 md:mt-8 bg-green-50 border border-green-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6">
          <div className="flex items-start gap-2 sm:gap-3">
            <FaInfoCircle className="text-green-600 text-base sm:text-lg md:text-xl mt-0.5 sm:mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-sm sm:text-base md:text-lg font-semibold text-green-800 mb-1 sm:mb-2">Transfer Initiated</h3>
              <p className="text-green-700 text-xs sm:text-sm md:text-base">{transferMessage}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnershipTransfer;
